import i18n from "i18next";
import { type FC, useEffect, useMemo } from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import type { AcquirerEntity } from "src/app/features/Acquirer/types/AcquirerEntity";
import { useManualDasConfigurationSwitchUpdateMutation } from "src/app/features/ManualDasConfiguration/services/server/ManualDasConfigurationApi";
import { Checkbox } from "../../../../components/Form/formComponents";
import type { FormProps } from "../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../core/setup/redux/state/notifications/notificationsState";

export const ManualDasConfigSwitchComponent: FC<FormProps> = ({ runSubmit }) => {
    const location = useLocation();
    const dasConfig = location.state?.config;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const allAcquirers: AcquirerEntity[] = useMemo(
        () => [...(dasConfig.activeDasAcquirers ?? []), ...(dasConfig.disabledAcquirers ?? [])],
        [dasConfig],
    );

    const allAcquirerIds = useMemo(() => allAcquirers.map((a) => a.acquirerId), [allAcquirers]);

    const [checkedIds, setCheckedIds] = useState<string[]>(
        dasConfig.activeDasAcquirers?.map((a: AcquirerEntity) => String(a.acquirerId)) ?? [],
    );

    const handleChange = (acquirer: AcquirerEntity, checkedValue: boolean) => {
        const acquirerId = String(acquirer.acquirerId);
        setCheckedIds((prev) => {
            if (checkedValue) {
                return prev.includes(acquirerId) ? prev : [...prev, acquirerId];
            }
            return prev.filter((a) => a !== acquirerId);
        });
    };

    const [switchAcquirers] = useManualDasConfigurationSwitchUpdateMutation();

    useEffect(() => {
        if (!runSubmit) {
            return;
        }

        const save = async () => {
            const acquirers = allAcquirerIds.join(",");

            const checkedAcquirers = checkedIds;

            const result = await switchAcquirers({
                data: {
                    orgUnit: dasConfig.id,
                    acquirers,
                    checkedAcquirers,
                },
            });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccessNoId", {
                            resource: "Manual DAS Configuration",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.MANUAL_DAS_CONFIGURATION}`);
            }
        };

        Promise.resolve().then(save);
    }, [allAcquirerIds, allAcquirers, checkedIds, dasConfig.id, dispatch, navigate, runSubmit, switchAcquirers]);

    return (
        <>
            {allAcquirers.map((acquirer) => (
                <div key={`${acquirer.name}-${acquirer.acquirerId}`} style={{ margin: "3px" }}>
                    <Checkbox
                        label={`${acquirer.name} (${acquirer.id})`}
                        value={checkedIds.includes(String(acquirer.acquirerId))}
                        name={`${acquirer.name}-${acquirer.acquirerId}`}
                        readOnly={false}
                        onChange={(e) => handleChange(acquirer, e)}
                    />
                </div>
            ))}
        </>
    );
};
